// src/app/store.js
import { configureStore, createListenerMiddleware } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

import authReducer, { logout, setCredentials } from "features/auth/authSlice";
import callApplicationReducer from "features/callApplicationSlice/callApplicationSlice";
import languageReducer from "features/language/languageSlice";

import { activeCaseApi } from "components/services/active-case/ActiveCaseService";
import { applicationApi } from "components/services/application/ApplicationService"; // Import the applicationApi
import { fileApi } from "components/services/application-file/ApplicationFile";
import { authApi } from "components/services/authorization/authService";
import { bankApi } from "components/services/bank/bankService";
import { callApplicationApi } from "components/services/call-application/CallApplicationService";
import { callApplicationFileApi } from "components/services/call-application-file/CallApplicationFile";
import { commentApi } from "components/services/comment/commentService";
import { contactInformationApi } from "components/services/contact-information/ContactInformationService";
import { feedbackApi } from "components/services/feedback/feedbackService";
import { messagesApi } from "components/services/messages/messagesService";
import { reportApi } from "components/services/report/reportService";
import { reportScheduleApi } from "components/services/report-schedule/reportScheduleService";
import { templateCommentApi } from "components/services/template-comment/templateCommentService";
import { templateFileApi } from "components/services/template-file/templateFileService";
import { userApi } from "components/services/users/userService";

// Every RTK Query API of the application. The store registers each one from this
// list, and a logout empties all of their caches.
export const apiSlices = [
  authApi,
  userApi,
  fileApi,
  callApplicationFileApi,
  callApplicationApi,
  applicationApi,
  commentApi,
  contactInformationApi,
  templateCommentApi,
  feedbackApi,
  messagesApi,
  reportScheduleApi,
  bankApi,
  templateFileApi,
  reportApi,
  activeCaseApi,
];

// The UI texts are the same for everyone, and the toasts read them from this
// cache on the sign-in page too; they are not data of the user.
const publicApiSlices = [messagesApi];

// Data loaded for a signed-in user stays in these caches until they are reset;
// after a logout nothing of it may be shown again (VAPT Portal 5).
export const clearApiCaches = () => (dispatch) => {
  apiSlices
    .filter((api) => !publicApiSlices.includes(api))
    .forEach((api) => dispatch(api.util.resetApiState()));
};

const session = createListenerMiddleware();
session.startListening({
  actionCreator: logout,
  effect: (_, { dispatch }) => dispatch(clearApiCaches()),
});

export const store = configureStore({
  reducer: {
    auth: authReducer,
    language: languageReducer,
    callApplication: callApplicationReducer,
    ...Object.fromEntries(
      apiSlices.map((api) => [api.reducerPath, api.reducer])
    ),
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .prepend(session.middleware)
      .concat(apiSlices.map((api) => api.middleware)),
});

const user = localStorage.getItem("user");
const accessToken = localStorage.getItem("accessToken");

if (user && accessToken) {
  store.dispatch(setCredentials({ user: JSON.parse(user), accessToken }));
}

// Optional: setup listeners for refetching queries on focus/reconnect
setupListeners(store.dispatch);
